import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import { CustomerSelectionVM } from '@app/customer/models/customer-selection-vm.model';
import { ModalController } from '@ionic/angular';
import { PlatformService } from '@panamax/app-state';
import { PaymentsModalService } from '@payments/services/payments-modal.service';
import { MustMatchValidator } from '@shared/validators/must-match.validator';
import { CustomerBankAccount, EligibleCustomer } from '@usf/payment-types';
import { Observable } from 'rxjs';
import { AutopaySteps } from './autopay-steps/autopay-steps.enum';
import { AutopayConfirmationForm } from './confirm-autopay-details/confirm-autopay-details.component';
import { BankToggleOptions } from './segments-bank-account/bank-toggle-options.enum';

@Component({
  selector: 'app-add-bank-account-modal',
  templateUrl: './add-bank-account-modal.component.html',
  styleUrls: ['./add-bank-account-modal.component.scss'],
})
export class AddBankAccountModalComponent implements OnInit, OnDestroy {
  @Input() bankAccounts: CustomerBankAccount[];
  @Input() canSave = true;
  @Input() canSelectCustomers: boolean;
  @Input() currentBankAccount: CustomerBankAccount;
  @Input() customerNumber: number;
  @Input() isEditingAutopay = false;
  @Input() isEditingBankAccount = false;
  @Input() isPrimaryByDefault = false;
  @Input() isSettingUpAutopay = false;
  @Input() showSegments: boolean;
  @Input() eligibleADCustomers: EligibleCustomer[];
  @Input() isBankRoutingValidationOn: boolean;

  readonly toggleOptions = BankToggleOptions;
  readonly autopaySteps = AutopaySteps;
  currentAutopayStep: number;
  selectedCustomers: CustomerSelectionOption[] = [];
  effectiveDate: Date;
  addBankAccountForm: FormGroup;
  selectedSegment = this.toggleOptions.newAccount;
  selectedBankAccount: CustomerBankAccount;
  vmWithCustomers$: Observable<CustomerSelectionVM>;
  autopayConfirmationValidate: AutopayConfirmationForm;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLoading = false;
  validationStatus: string;

  constructor(
    readonly platformService: PlatformService,
    private modalController: ModalController,
    public paymentsModalService: PaymentsModalService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.isMobile =
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.mobile;
    this.isTablet =
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.tablet;
    this.isDesktop =
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.desktop;
    this.selectedSegment =
      this.showSegments && this.bankAccounts.length > 0
        ? this.toggleOptions.existingAccount
        : this.toggleOptions.newAccount;

    this.selectedBankAccount = this.currentBankAccount;

    if (this.canSelectCustomers)
      this.vmWithCustomers$ = this.paymentsModalService.getCustomerSelectionVM$(
        true,
        this.eligibleADCustomers?.map(customer => customer.customerNumber),
      );

    this.currentAutopayStep = this.isSettingUpAutopay
      ? this.autopaySteps.stepOne
      : 0;

    this.createForm();
  }

  createForm(): void {
    const checkingAccountUpdateTiming = this.isBankRoutingValidationOn
      ? 'change'
      : 'submit';
    this.addBankAccountForm = this.fb.group(
      {
        nickName: new FormControl(
          this.isEditingAutopay || this.isEditingBankAccount
            ? this.currentBankAccount?.nickName
            : '',
        ),
        routingNumber: new FormControl('', {
          validators: [
            removeNonDigitsValidator(),
            Validators.required,
            Validators.minLength(9),
            Validators.maxLength(9),
          ],
        }),
        bankName: new FormControl('', { validators: Validators.required }),
        checkingAccountNumber: new FormControl('', {
          validators: [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(17),
            removeNonDigitsValidator(),
          ],
          updateOn: checkingAccountUpdateTiming,
        }),
        confirmCheckingAccountNumber: new FormControl('', {
          validators: [Validators.required, removeNonDigitsValidator()],
          updateOn: checkingAccountUpdateTiming,
        }),
        agreementCheckbox: new FormControl(false, {
          validators: this.isSettingUpAutopay ? [] : Validators.requiredTrue,
        }),
        isPrimary: new FormControl({
          value:
            this.isEditingAutopay || this.isEditingBankAccount
              ? this.currentBankAccount?.isPrimary
              : this.isPrimaryByDefault,
          disabled: this.isPrimaryByDefault,
        }),
        validationStatus: new FormControl(
          this.isBankRoutingValidationOn ? '' : 'Off',
        ),
      },
      {
        validators: MustMatchValidator(
          'checkingAccountNumber',
          'confirmCheckingAccountNumber',
        ),
      },
    );
  }

  async dismissModal(
    isSaved: boolean = false,
    bankAccount?: Partial<CustomerBankAccount> & {
      customerNumbers?: number[];
      email?: string;
      fullName?: string;
    },
  ) {
    await this.modalController.dismiss({ isSaved, bankAccount });
  }

  isFormLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  submitSelectedBankAccountData() {
    if (
      this.selectedSegment === this.toggleOptions.existingAccount &&
      this.selectedBankAccount
    ) {
      this.dismissModal(false, {
        ...this.selectedBankAccount,
        customerNumber: this.customerNumber,
        paymentMethod: 'MD',
      });
    }
  }

  submitAutopayData() {
    this.dismissModal(true, {
      customerNumbers: this.selectedCustomers.map(
        customer => customer.customerNumber,
      ),
      email: this.autopayConfirmationValidate.email,
      fullName: this.autopayConfirmationValidate.fullName,
      autoDeductStartDate: +this.effectiveDate
        .toJSON()
        .replace(/-/g, '')
        .substring(0, 8),
      ...(this.selectedSegment === this.toggleOptions.existingAccount
        ? {
            bankID: this.selectedBankAccount.bankID,
            validationStatus: null,
          }
        : {
            bankName: this.addBankAccountForm.controls.bankName.value,
            nickName: this.addBankAccountForm.controls.nickName.value,
            accountNumber:
              this.addBankAccountForm.controls.checkingAccountNumber.value,
            routingNumber: this.addBankAccountForm.controls.routingNumber.value,
            validationStatus:
              this.addBankAccountForm.controls.validationStatus.value,
          }),
    });
  }

  submitBankAccountData() {
    this.addBankAccountForm.markAllAsTouched(); // Highlights errors after validation
    if (
      this.isEditingAutopay ||
      this.isEditingBankAccount ||
      this.addBankAccountForm.valid
    ) {
      const bankAccount = {
        customerNumbers: this.canSelectCustomers
          ? this.selectedCustomers.map(customer => customer.customerNumber)
          : [this.customerNumber],
        bankID:
          this.isEditingAutopay || this.isEditingBankAccount
            ? this.currentBankAccount?.bankID
            : undefined,
        nickName: this.addBankAccountForm.controls.nickName.value?.trim() || '',
        isPrimary: this.isEditingAutopay
          ? true
          : this.addBankAccountForm.controls.isPrimary.value,
        paymentMethod: this.isEditingAutopay ? 'AD' : 'MD',

        ...(this.isEditingAutopay || this.isEditingBankAccount
          ? {
              validationStatus: null,
            }
          : {
              routingNumber:
                this.addBankAccountForm.controls.routingNumber.value,
              accountNumber:
                this.addBankAccountForm.controls.checkingAccountNumber.value,
              bankName: this.addBankAccountForm.controls.bankName.value,
              validationStatus:
                this.addBankAccountForm.controls.validationStatus.value,
            }),
      };
      this.dismissModal(true, bankAccount);
    }
  }

  setSelectedSegment(segment: BankToggleOptions) {
    this.selectedSegment = segment;
  }

  selectBankAccount(bankAccount: CustomerBankAccount) {
    this.selectedBankAccount = bankAccount;
  }

  selectEffectiveDate(date: Date) {
    this.effectiveDate = date;
  }

  selectCustomers(newSelectedCustomers: CustomerSelectionOption[]) {
    this.selectedCustomers = newSelectedCustomers;
  }

  setAutopayConfirmation(confirmationForm: AutopayConfirmationForm) {
    this.autopayConfirmationValidate = confirmationForm;
  }

  changeAutopayStep(
    newStep: number,
    shouldValidateAccountForm: boolean = false,
  ): void {
    if (
      shouldValidateAccountForm &&
      this.currentAutopayStep === this.autopaySteps.stepOne &&
      this.selectedSegment === this.toggleOptions.newAccount
    )
      return;
    if (newStep === 0) {
      this.dismissModal();
      return;
    }
    this.currentAutopayStep = newStep;
  }

  ngOnDestroy() {
    this.paymentsModalService.searchText$.next('');
  }

  get disableAutopayNextButton(): boolean {
    return (
      (this.currentAutopayStep === this.autopaySteps.stepOne &&
        this.selectedSegment === this.toggleOptions.existingAccount &&
        !this.selectedBankAccount) ||
      (this.currentAutopayStep === this.autopaySteps.stepOne &&
        this.selectedSegment === this.toggleOptions.newAccount &&
        (this.addBankAccountForm.controls?.routingNumber.hasError(
          'invalidRoutingNumber',
        ) ||
          Object.keys(this.addBankAccountForm.controls?.bankName.errors || {})
            .length > 0)) ||
      (this.currentAutopayStep === this.autopaySteps.stepTwo &&
        this.selectedCustomers.length === 0) ||
      (this.currentAutopayStep === this.autopaySteps.stepThree &&
        (!this.effectiveDate ||
        this.selectedCustomers.length === 0 ||
        this.selectedSegment === this.toggleOptions.existingAccount
          ? !this.selectedBankAccount
          : this.addBankAccountForm.invalid))
    );
  }

  get shouldShowBackAutopayStepButton(): boolean {
    return (
      (this.isMobile &&
        !!this.currentAutopayStep &&
        this.currentAutopayStep !== this.autopaySteps.stepOne) ||
      this.currentAutopayStep === this.autopaySteps.confirmAutopayDetails
    );
  }

  get isSaveDisabled() {
    if (!this.canSave) return true;
    if (this.isEditingAutopay || this.isEditingBankAccount) return false;
    if (
      this.addBankAccountForm.controls?.routingNumber.hasError(
        'invalidRoutingNumber',
      )
    )
      return true;
    return (
      (!this.isSettingUpAutopay &&
        (this.selectedSegment === this.toggleOptions.newAccount
          ? !this.addBankAccountForm.controls?.agreementCheckbox.value
          : !this.selectedBankAccount)) ||
      (this.canSelectCustomers && this.selectedCustomers.length === 0) ||
      (this.currentAutopayStep === this.autopaySteps.confirmAutopayDetails &&
        !this.autopayConfirmationValidate?.isValid)
    );
  }
}

function removeNonDigitsValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) {
      return null;
    }

    const cleanValue = control.value.replace(/\D/g, '');
    if (control.value !== cleanValue) {
      control.setValue(cleanValue, { emitEvent: false });
    }

    return null;
  };
}
